// frontend/src/components/ContentBlock.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import QuestionComponent from './QuestionComponent';
import StreamedHTML from './StreamedHTML';
import TableCellComponent from './TableCellComponent'; // Import the new component



// Add blockRef to props
function ContentBlock({ block, index, chapterId, totalBlocks, userAnswers, updateAnswer, onInView, blockRef }) {
  // Use the passed blockRef with useInView
  const { ref: inViewRefInternal, inView } = useInView({ threshold: 0.6 });

  // Combine the internal ref logic with the external blockRef
  const combinedRef = (el) => {
    inViewRefInternal(el); // Call useInView's ref function
    if (blockRef) { // If an external ref is provided
      if (typeof blockRef === 'function') {
        blockRef(el);
      } else {
        blockRef.current = el;
      }
    }
  };

  const existingAnswer = userAnswers[block._id];
  const { token } = useContext(AuthContext); // Ensure token is destructured if not already
  const [hoveredTooltip, setHoveredTooltip] = useState(null); // This state remains here

  // For AI chat functionality
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Add state for table maximization
  const [isTableMaximized, setIsTableMaximized] = useState(false);

  // ------------------ AI Chat Effects ------------------
  useEffect(() => {
    // If block is AI, load welcome message if it exists
    if (block.type === 'ai' && block.data.welcomeMessage && chatMessages.length === 0) {
      setChatMessages([{ role: 'assistant', content: block.data.welcomeMessage }]);
    }
  }, [block, chatMessages.length]);

  useEffect(() => {
    // Scroll down on new chat messages
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    // Load existing chat from userAnswers if available
    if (block.type === 'ai' && Array.isArray(existingAnswer)) {
      setChatMessages(existingAnswer);
    }
  }, [block.type, existingAnswer]);

  useEffect(() => {
    // If AI is typing but block not in view, scroll to it
    if (block.type === 'ai' && isTyping && !inView) {
      const currentBlock = combinedRef.current;
      if (currentBlock) {
        currentBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isTyping, block.type, inView]);

  // Effect to call the callback when the block comes into view
  useEffect(() => {
    if (inView && onInView) {
      onInView(index);
    }
  }, [inView, index, onInView]);

  // ------------------ AI Chat: Send Message ------------------
  const handleSendMessage = async () => {
    if (!userInput.trim()) return;
    const scrollPosition = window.scrollY;
    const newMessages = [...chatMessages, { role: 'user', content: userInput }];

    setChatMessages(newMessages);
    updateAnswer(block._id, newMessages);
    setUserInput("");
    setIsTyping(true);

    // Lock scroll
    window.scrollTo({ top: scrollPosition, behavior: 'auto' });
    setTimeout(() => {
      window.scrollTo({ top: scrollPosition, behavior: 'auto' });
    }, 50);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          chapterId,
          contentBlockId: block._id,
          systemPrompt: block.data.systemPrompt,
          messages: newMessages
        })
      });

      const data = await response.json();
      if (response.ok) {
        const updatedMessages = [...newMessages, { role: 'assistant', content: data.message }];
        setChatMessages(updatedMessages);
        updateAnswer(block._id, updatedMessages);
      } else {
        const fallback = [...newMessages, { role: 'assistant', content: "Sorry, I encountered an error. Please try again." }];
        setChatMessages(fallback);
        updateAnswer(block._id, fallback);
      }
    } catch (error) {
      const fallback = [...newMessages, { role: 'assistant', content: "Sorry, I encountered an error. Please try again." }];
      setChatMessages(fallback);
      updateAnswer(block._id, fallback);
    } finally {
      setIsTyping(false);
      window.scrollTo({ top: scrollPosition, behavior: 'auto' });
    }
  };

  // ------------------ Table: Single Auto-fill for entire table ------------------
  const handleAutoFillTable = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/autofill/${block._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          chapterId,
          tableData: block.data, // send entire table data
        }),
      });

      const data = await response.json();
      console.log("Auto-fill response:", data);
      if (response.ok) {
        // data.autoFill should contain the entire updated table
        // We'll replace block.data with the returned one
        const updatedTable = data.autoFill; 
        // e.g. { headings: [...], rows: [...], ... }

        // Then call updateAnswer to store it
        // (Might want to ensure we keep autoFillCells/frozenCells if GPT didn't remove them)
        updateAnswer(block._id, updatedTable);
      } else {
        console.error("Error generating auto-fill:", data.error);
      }
    } catch (err) {
      console.error("Error during auto-fill call:", err);
    }
  };

  // ------------------ Render Logic ------------------
  const renderContent = () => {
    switch (block.type) {
      case 'text':
        return (
          <StreamedHTML
            htmlString={block.data}
            speed={20} // Adjust speed as needed (milliseconds per character)
            streamIncrement={3} // Number of characters to reveal per frame
            startCondition={inView}
            className="my-4 px-4 font-body text-3xl"
          />
        );

      case 'image':
        return (
          <img src={block.data} alt={`Image ${index + 1}`} className="my-4 mx-auto" />
        );

      case 'question':
        return (
          <div className="my-4 font-body text-3xl">
            <QuestionComponent
              question={block.data}
              chapterId={chapterId}
              contentBlockId={block._id}
              existingAnswer={existingAnswer}
              onAnswerChange={updateAnswer}
            />
          </div>
        );

      case 'table': {
        const [tableData, setTableData] = useState(() => userAnswers[block._id] || block.data);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [saveStatus, setSaveStatus] = useState('');
        const [autoFillingCells, setAutoFillingCells] = useState({});
        const firstRender = useRef(true);

        useEffect(() => {
          // block.data is the definitive structure (headings, frozenCells, autoFillCells, tooltips, autoFillPrompt)
          // userAnswers[block._id]?.rows contains the user's actual cell inputs.
        
          const userSavedData = userAnswers[block._id];
        
          // Start with the latest definition from block.data for all structural properties
          let newTableState = {
            headings: block.data.headings || [],
            rows: [], // Will be populated based on user answers or block definition
            frozenCells: block.data.frozenCells || {},
            autoFillCells: block.data.autoFillCells || {},
            tooltips: block.data.tooltips || {},
            fixedColumns: block.data.fixedColumns || {},
            autoFillPrompt: block.data.autoFillPrompt || '',
            textContent: block.data.textContent || '',
            selectedColumn: userSavedData?.selectedColumn !== undefined ? userSavedData.selectedColumn : (block.data.selectedColumn !== undefined ? block.data.selectedColumn : null),
            // Remove referenceTableBlockId initialization
            // Include any other structural properties from block.data if they exist
          };
        
          const definitionRows = block.data.rows || [];
          const definitionHeadings = newTableState.headings;
          const numCols = definitionHeadings.length;
        
          // Use user's saved rows if available, otherwise use definition rows from block.data
          const rowsSource = (userSavedData?.rows && userSavedData.rows.length > 0) ? userSavedData.rows : definitionRows;
        
          newTableState.rows = rowsSource.map(sourceRow => {
            const newRow = Array(numCols).fill(''); // Initialize new row based on definitive column count
            if (Array.isArray(sourceRow)) {
              for (let i = 0; i < numCols; i++) {
                if (sourceRow[i] !== undefined) {
                  newRow[i] = sourceRow[i]; // Take cell value from source
                }
              }
            }
            return newRow;
          });
        
          // If the number of rows in block.data is different from rowsSource, adjust.
          // This ensures that if admin adds/removes rows, the table reflects that structure.
          if (definitionRows.length !== newTableState.rows.length) {
            const adjustedRows = Array(definitionRows.length).fill(null).map((_, rIndex) => {
              if (newTableState.rows[rIndex]) { // If user data exists for this row index
                // Ensure it has the correct number of columns
                const rowToAdjust = newTableState.rows[rIndex];
                const finalAdjustedRow = Array(numCols).fill('');
                for(let cIndex = 0; cIndex < numCols; cIndex++) {
                  if (rowToAdjust[cIndex] !== undefined) {
                    finalAdjustedRow[cIndex] = rowToAdjust[cIndex];
                  }
                }
                return finalAdjustedRow;
              }
              // If no user data for this row index, use the definition row (padded)
              const defRow = definitionRows[rIndex] || [];
              const finalDefRow = Array(numCols).fill('');
              for(let cIndex = 0; cIndex < numCols; cIndex++) {
                if (defRow[cIndex] !== undefined) {
                  finalDefRow[cIndex] = defRow[cIndex];
                }
              }
              return finalDefRow;
            });
            newTableState.rows = adjustedRows;
          }

          // Only update the local state if the newly constructed state is different
          // from the current tableData. This prevents unnecessary re-renders.
          if (JSON.stringify(newTableState) !== JSON.stringify(tableData)) {
            setTableData(newTableState);
          }
        }, [block.data, userAnswers, block._id]);

        // Autosave effect
        useEffect(() => {
          if (firstRender.current) {
            firstRender.current = false;
            return;
          }
          if (!tableData) return;
          if (updateAnswer) {
            updateAnswer(block._id, tableData);
          }
          const timer = setTimeout(() => {
            setSaveStatus('Saving...');
            saveTableToServer(tableData);
          }, 500);
          return () => clearTimeout(timer);
        }, [tableData]); // This should already work, but let's verify tableData is updating properly

        const saveTableToServer = async (dataToSave) => {
          try {
            setIsSubmitting(true);
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/user/submit-answer`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                chapterId,
                contentBlockId: block._id,
                answer: dataToSave, 
              }),
            });
  
            if (!res.ok) throw new Error('Failed to save table');
            setSaveStatus('Saved.');
          } catch (error) {
            console.error('Error saving table:', error);
            setSaveStatus('Error saving table');
          } finally {
            setIsSubmitting(false);
          }
        };

        const handleCellChange = (rowIndex, colIndex, newValue) => {
          const updated = { ...tableData, rows: [...(tableData.rows || [])] };
          updated.rows[rowIndex] = [...(updated.rows[rowIndex] || [])];
          updated.rows[rowIndex][colIndex] = newValue;
          setTableData(updated);
        };

        const handleAutoFillTable = async () => {
          if (!tableData || !tableData.rows || !tableData.autoFillCells) return;

          const cellsToLoad = {};
          (tableData.rows || []).forEach((row, rIndex) => {
            (row || []).forEach((cell, cIndex) => {
              const cellKey = `${rIndex}-${cIndex}`;
              // Mark for loading if it's an autoFillCell AND it's currently empty
              if (tableData.autoFillCells[cellKey] && (cell === null || cell === '')) {
                cellsToLoad[cellKey] = true;
              }
            });
          });

          if (Object.keys(cellsToLoad).length === 0) {
            setSaveStatus("No empty cells marked for auto-fill.");
            setTimeout(() => setSaveStatus(''), 2000);
            return;
          }
          
          setAutoFillingCells(cellsToLoad);
          setIsSubmitting(true);
          setSaveStatus('Auto-filling...');

          try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/autofill/${block._id}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                chapterId,
                tableData, 
              }),
            });

            if (!response.ok) {
              throw new Error('Failed to start auto-fill streaming');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let streamedContent = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop(); // Keep incomplete line in buffer

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    
                    if (data.type === 'progress' && data.content) {
                      streamedContent += data.content;
                      // Update status to show streaming progress
                      setSaveStatus(`Auto-filling... (${streamedContent.length} characters received)`);
                    } else if (data.type === 'complete' && data.autoFill) {
                      // Final result received
                      const updatedTable = data.autoFill;
                      setTableData(updatedTable);
                      if (updateAnswer) { 
                        updateAnswer(block._id, updatedTable);
                      }
                      setSaveStatus('Auto-fill complete.');
                      setAutoFillingCells({});
                      setIsSubmitting(false);
                      setTimeout(() => setSaveStatus(''), 3000);
                      return;
                    } else if (data.type === 'error') {
                      throw new Error(data.error || 'Auto-fill error');
                    }
                  } catch (parseError) {
                    console.error('Error parsing SSE data:', parseError);
                  }
                }
              }
            }
          } catch (err) {
            console.error("Error during auto-fill:", err);
            setSaveStatus(`Auto-fill error: ${err.message || 'Unknown error'}`);
            setAutoFillingCells({});
            setIsSubmitting(false);
            setTimeout(() => setSaveStatus(''), 3000);
          }
        };

        const COLUMN_WIDTH = 350; // Define a constant column width in pixels

        const calculateStickyLeftOffset = (currentColIndex, fixedColumnsMap) => {
          let offset = 0;
          for (let i = 0; i < currentColIndex; i++) {
            if (fixedColumnsMap && fixedColumnsMap[i]) {
              offset += COLUMN_WIDTH;
            }
          }
          return `${offset}px`;
        };

        if (!tableData || !tableData.rows) {
          return <div>No table data available or table structure is invalid.</div>;
        }

        // Function to render the table content
        const renderTableContent = () => (
          <table className="min-w-full w-full text-left border-collapse">
            <thead className="bg-gray-100">
              <tr>
                {(tableData.headings || []).map((heading, i) => {
                  const isFixed = tableData.fixedColumns?.[i];
                  const isSelected = tableData.selectedColumn === i;
                  const thStyle = {
                    width: `${COLUMN_WIDTH}px`,
                    minWidth: `${COLUMN_WIDTH}px`,
                  };
                  if (isFixed) {
                    thStyle.position = 'sticky';
                    thStyle.left = calculateStickyLeftOffset(i, tableData.fixedColumns);
                    thStyle.zIndex = 2;
                    thStyle.backgroundColor = 'rgb(243 244 246)';
                  }
                  return (
                    <th
                      key={i}
                      style={thStyle}
                      className={`px-6 py-3 border-b border-gray-200 text-gray-600 font-semibold text-sm tracking-wider cursor-pointer transition-colors ${
                        isSelected ? 'bg-[#95dbcb]' : 'hover:bg-gray-200'
                      }`}
                      onClick={() => handleColumnSelect(i)}
                      title={`Click to ${isSelected ? 'deselect' : 'select'} column`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{heading}</span>
                        {isSelected && (
                          <svg 
                            className="w-4 h-4 text-black ml-2" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(tableData.rows || []).map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}
                >
                  {(row || []).map((cell, colIndex) => {
                    const cellKey = `${rowIndex}-${colIndex}`;
                    const isCellFrozenByFreezeFeature = tableData.frozenCells?.[cellKey];
                    const tooltipData = tableData.tooltips?.[cellKey];
                    const isCurrentlyAutoFilling = autoFillingCells[cellKey] === true;
                    const isColumnFixed = tableData.fixedColumns?.[colIndex];
                    const isColumnSelected = tableData.selectedColumn === colIndex;
                    const cellBaseStyle = {
                      width: `${COLUMN_WIDTH}px`,
                      minWidth: `${COLUMN_WIDTH}px`,
                    };
                    let cellFinalStyle = { ...cellBaseStyle };

                    if (isColumnFixed) {
                      cellFinalStyle = {
                        ...cellFinalStyle,
                        position: 'sticky',
                        left: calculateStickyLeftOffset(colIndex, tableData.fixedColumns),
                        zIndex: 1,
                        backgroundColor: isCellFrozenByFreezeFeature || isCurrentlyAutoFilling
                          ? 'rgb(243 244 246)'
                          : isColumnSelected
                          ? 'rgb(213 237 232)' // Light blue for selected column
                          : (rowIndex % 2 === 0 ? 'white' : 'rgb(249 250 251)'),
                      };
                    } else if (isColumnSelected) {
                      cellFinalStyle = {
                        ...cellFinalStyle,
                        backgroundColor: isCellFrozenByFreezeFeature || isCurrentlyAutoFilling
                          ? 'rgb(243 244 246)'
                          : 'rgb(213 237 232)', // Light blue for selected column
                      };
                    }

                    return (
                      <TableCellComponent
                        key={cellKey}
                        cellValue={cell}
                        isFrozen={isCellFrozenByFreezeFeature || isCurrentlyAutoFilling}
                        tooltipData={tooltipData}
                        cellKey={cellKey}
                        onCellChange={handleCellChange}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                        hoveredTooltip={hoveredTooltip}
                        setHoveredTooltip={setHoveredTooltip}
                        isAutoFilling={isCurrentlyAutoFilling}
                        stickyStyle={cellFinalStyle}
                        isColumnSelected={isColumnSelected}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        );

        const handleColumnSelect = (colIndex) => {
          const newSelectedColumn = tableData.selectedColumn === colIndex ? null : colIndex;
          const updated = { 
            ...tableData, 
            selectedColumn: newSelectedColumn
          };
          console.log('Column selection changed:', { from: tableData.selectedColumn, to: newSelectedColumn }); // Debug log
          setTableData(updated);
        };

        return (
          <>
            <div className="my-4 px-4 font-body">
              {/* Text Content Section */}
              {tableData.textContent && (
                <div 
                  className="font-body mb-6 text-3xl leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: tableData.textContent }}
                />
              )}
              
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={handleAutoFillTable}
                  className="bg-black text-white px-4 py-2 rounded-md font-body text-sm hover:bg-gray-800 transition disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  Auto-fill Table
                </button>
                <button
                  onClick={() => setIsTableMaximized(true)}
                  className="bg-white text-black border border-black px-4 py-2 rounded-md font-body text-sm transition flex items-center gap-2"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className="w-4 h-4"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                  Maximize
                </button>
              </div>
              
              <div
                className="overflow-x-auto overflow-y-auto shadow-sm rounded-lg border border-gray-200"
                style={{ maxHeight: '425px' }}
              >
                {renderTableContent()}
              </div>
              <div className="text-sm mt-4 h-5 text-gray-500">{saveStatus}</div>
            </div>

            {/* Maximized Table Modal */}
            {isTableMaximized && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
                <div className="font-body mx-8 bg-white rounded-lg shadow-xl w-full h-full max-w-full max-h-[90vh] flex flex-col">
                  {/* Modal Header */}
                  <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <button
                      onClick={handleAutoFillTable}
                      className="bg-black text-white px-3 py-1.5 rounded-md text-sm hover:bg-gray-800 transition disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      Auto-fill Table
                    </button>
                    <button
                      onClick={() => setIsTableMaximized(false)}
                      className="bg-white text-black border border-black border-1 px-3 py-1.5 rounded-md text-sm transition flex items-center gap-1"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth={1.5} 
                        stroke="currentColor" 
                        className="w-4 h-4"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                      </svg>
                      Minimize
                    </button>
                  </div>
                  
                  {/* Modal Content */}
                  <div className="flex-1 overflow-auto p-4">
                    {/* Text Content in Modal */}
                    
                    
                    <div className="overflow-auto border border-gray-200 rounded-lg">
                      {renderTableContent()}
                    </div>
                    {saveStatus && (
                      <div className="text-sm mt-4 text-gray-500">{saveStatus}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      }
        
        

      case 'ai':
        return (
          <div className="my-4">
            <div className="bg-white rounded-lg shadow-md p-4" style={{ position: 'relative' }}>
              <div
                className="h-[400px] overflow-y-auto mb-4 p-2 border rounded"
                onClick={(e) => e.stopPropagation()}
              >
                {chatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}
                  >
                    <div
                      className={`inline-block p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-800'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="text-left">
                    <div className="inline-block p-3 rounded-lg bg-gray-200">
                      <span className="animate-pulse">Typing...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="flex">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-grow p-2 border rounded-l"
                  onClick={(e) => e.stopPropagation()}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSendMessage();
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-r"
                  disabled={isTyping}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        );

      case 'lastSlide': {
        const { adjustedUnansweredIndexes = [] } = block;
        const stillIncomplete = adjustedUnansweredIndexes.length > 0;
        return (
          <div className="font-body text-3xl my-4 px-4 text-center">
            {stillIncomplete ? (
              <>
                <p className="mb-4 text-red-500">
                  Please complete slides {adjustedUnansweredIndexes.join(', ')} before accessing Feedback.
                </p>
                <a
                  href="#"
                  className="mt-6 bg-black text-white px-4 py-2 rounded inline-block cursor-not-allowed"
                  onClick={(e) => e.preventDefault()}
                >
                  Go to Feedback
                </a>
              </>
            ) : (
              <>
                <p className="mb-4">All questions answered!</p>
                <a
                  href={`/feedback/${chapterId}`}
                  className="mt-6 bg-black text-white px-4 py-2 rounded inline-block"
                >
                  Go to Feedback
                </a>
              </>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <motion.div
      ref={combinedRef}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0, y: 20 }, // Fade out and slightly down
        visible: { opacity: 1, y: 0 },  // Fade in and to original position
      }}
      transition={{ duration: 0.5, ease: 'easeOut' }} // Smoother transition
      className="content-block flex items-center justify-center relative w-full" // Added w-full for clarity
      style={{ height: '100%', flexShrink: 0 }} // Ensure it takes full height and doesn't shrink if in a flex column
    >
      {/* This inner div now controls the content's width and its actual top padding. */}
      <div className="w-2/3">
        {renderContent()}
      </div>
      {/* Slide count removed from here */}
    </motion.div>
  );
}

export default ContentBlock;
